import React from 'react';
import { Link } from 'react-router-dom';
import { MailIcon, MapPinIcon, CalendarIcon, CheckIcon } from 'lucide-react';
import { Container, Section, Kicker } from '../components/ui/Primitives';
import { Button } from '../components/ui/Button';
import { Field, inputClass, ConsentCheckbox } from '../components/ui/Field';
import { MeetingScheduler } from '../components/MeetingScheduler';
import { SERVICE_LINES } from '../data/services';
import { submitLead } from '../lib/leads';
import { track } from '../lib/analytics';
const ENQUIRY_TYPES = [{
  id: 'intelligence',
  label: 'A market or sector question'
}, {
  id: 'growth',
  label: 'Growth, pricing or pipeline'
}, {
  id: 'ai',
  label: 'AI adoption or readiness'
}, {
  id: 'other',
  label: 'Something else'
}];
export function Contact() {
  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [organisation, setOrganisation] = React.useState('');
  const [enquiry, setEnquiry] = React.useState('intelligence');
  const [message, setMessage] = React.useState('');
  const [consent, setConsent] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [sent, setSent] = React.useState(false);
  const routedLine = SERVICE_LINES.find(line => line.id === enquiry);
  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    const result = await submitLead({
      email,
      firstName: name,
      organisation,
      consent,
      source: 'contact-form',
      line: enquiry === 'other' ? undefined : enquiry,
      notes: message
    });
    setSubmitting(false);
    if (!result.ok) {
      setError(result.error ?? 'Something went wrong. Please try again.');
      return;
    }
    setSent(true);
  };
  return <>
      <section className="surface-dark" data-id="element-565" data-mp="2bv8zojc44glv">
        <Container className="py-14 sm:py-20" data-id="element-566" data-mp="2bu7lw73ppg0g">
          <Kicker data-id="element-567" data-mp="1m7da59f0r7ds">Contact</Kicker>
          <h1 className="mt-4 max-w-3xl font-display text-[34px] leading-tight text-white sm:text-[46px]" data-id="element-568" data-mp="1m6c0989qoqxn">
            Tell us the decision in front of you
          </h1>
          <p className="mt-6 max-w-2xl text-[17px] text-white/72" data-id="element-569" data-mp="1w59m23fs66cy">
            A discovery call lasts thirty minutes. We will tell you honestly whether we are the right firm for the
            problem, and what we would do first if we are.
          </p>
        </Container>
      </section>

      <Section data-id="element-570" data-mp="yhst4w2fek75">
        <div className="grid gap-12 lg:grid-cols-[1.1fr_0.9fr]" data-id="element-571" data-mp="yiaxkfz8s4q5">
          <div data-id="element-572" data-mp="yfoh1qudievs">
            <h2 className="font-display text-[26px] text-ink" data-id="element-573" data-mp="1d6oy2k96rh3j">Send us a message</h2>
            {sent ? <div className="mt-6 rounded-md border border-line bg-mist p-7" data-id="element-574" data-mp="18g8iathw7je0">
                <div className="flex items-center gap-2 text-gold" data-id="element-575" data-mp="18e45fm20na1l">
                  <CheckIcon className="h-5 w-5" aria-hidden="true" data-id="element-576" data-mp="koteewagwu0n" />
                  <span className="text-[11px] font-semibold uppercase tracking-[0.2em]" data-id="element-577" data-mp="fs2xk6qu9ue6">Received</span>
                </div>
                <h3 className="mt-4 font-display text-[22px] text-ink" data-id="element-578" data-mp="2rd24ief5ulv5">Thank you, we have your enquiry</h3>
                <p className="mt-3 text-[15px] text-body" data-id="element-579" data-mp="2w6o6ncnp77cp">
                  {routedLine ? `We have routed this to the ${routedLine.name} team. ` : 'We have routed this to the right team. '}
                  You will hear from us within one working day. If it is urgent, book a call using the scheduler.
                </p>
              </div> : <form onSubmit={onSubmit} className="mt-6 space-y-5" data-id="element-580" data-mp="3f3fwk4xqwkpq">
                <div className="grid gap-5 sm:grid-cols-2" data-id="element-581" data-mp="313empzzi62y4">
                  <Field id="c-name" label="Your name" required data-id="element-582" data-mp="3561gw7jnbzyh">
                    <input id="c-name" type="text" required value={name} onChange={event => setName(event.target.value)} className={inputClass} data-id="element-583" data-mp="3548a8s919fqh" />
                  </Field>
                  <Field id="c-email" label="Work email" required error={error ?? undefined} data-id="element-584" data-mp="29zu1hihd1rw3">
                    <input id="c-email" type="email" required value={email} onChange={event => setEmail(event.target.value)} placeholder="you@organisation.org" className={inputClass} data-id="element-585" data-mp="255hgh408k8i8" />
                  </Field>
                </div>
                <Field id="c-org" label="Organisation" required data-id="element-586" data-mp="2iypat3fpbi3m">
                  <input id="c-org" type="text" required value={organisation} onChange={event => setOrganisation(event.target.value)} className={inputClass} data-id="element-587" data-mp="2iww1thhm8lsg" />
                </Field>
                <Field id="c-enquiry" label="What is your enquiry about" required hint="We use this to route your message to the right service line." data-id="element-588" data-mp="13kbdkscphhne">
                  <select id="c-enquiry" value={enquiry} onChange={event => setEnquiry(event.target.value)} className={inputClass} data-id="element-589" data-mp="3o5qgzpjcsfzq">
                    {ENQUIRY_TYPES.map(type => <option key={type.id} value={type.id} data-id="element-590" data-mp="6oz6zmc7mfec">
                        {type.label}
                      </option>)}
                  </select>
                </Field>
                <Field id="c-message" label="Your message" required data-id="element-591" data-mp="fqox8oucv8aj">
                  <textarea id="c-message" required rows={5} value={message} onChange={event => setMessage(event.target.value)} placeholder="The decision you are facing, and any timing we should know about." className={inputClass} data-id="element-592" data-mp="fnszb0iicjm3" />
                </Field>
                <ConsentCheckbox id="c-consent" checked={consent} onChange={setConsent} data-id="element-593" data-mp="3tsvxf1dkzsr2">
                  I am happy for Cavalla Solution to contact me about this enquiry. See our{' '}
                  <Link to="/legal/privacy" className="underline underline-offset-2" data-id="element-594" data-mp="7lxnw3ae0n8n">
                    privacy notice
                  </Link>
                  .
                </ConsentCheckbox>
                <Button type="submit" size="lg" disabled={submitting} data-id="element-595" data-mp="ge7z9nx0qyrh">
                  {submitting ? 'Sending' : 'Send enquiry'}
                </Button>
              </form>}
          </div>

          <div className="space-y-8" data-id="element-596" data-mp="qcf3r7a4focl">
            <div className="rounded-md border border-line bg-mist p-6 sm:p-7" data-id="element-597" data-mp="qdgoy4kupr81">
              <div className="flex items-center gap-2 text-gold" data-id="element-598" data-mp="lduajrtae4xn">
                <CalendarIcon className="h-4 w-4" aria-hidden="true" data-id="element-599" data-mp="lbhlyvt0wwbm" />
                <span className="text-[11px] font-semibold uppercase tracking-[0.2em]" data-id="element-600" data-mp="14znhofute7s9">Scheduler</span>
              </div>
              <h2 className="mt-4 font-display text-[22px] text-ink" data-id="element-601" data-mp="19vuqcgelhxdw">Book a discovery call</h2>
              <p className="mt-3 text-[15px] text-body" data-id="element-602" data-mp="v30xltabbkra">
                Thirty minutes with our founder. Choose a slot that suits your time zone.
              </p>
              <MeetingScheduler line={enquiry} className="mt-6" data-id="element-603" data-mp="m7upgnnkkqll" />
            </div>

            <div className="rounded-md border border-line p-6 sm:p-7" data-id="element-604" data-mp="qzl1qz62hofn">
              <h2 className="font-display text-[20px] text-ink" data-id="element-605" data-mp="caoydfeygzld">Reach us directly</h2>
              <ul className="mt-5 space-y-4 text-[15px] text-body" data-id="element-606" data-mp="c8lym3o03s3g">
                <li className="flex gap-3" data-id="element-607" data-mp="ha1hbp2phmio">
                  <MailIcon className="mt-1 h-4 w-4 shrink-0 text-gold" aria-hidden="true" data-id="element-608" data-mp="3a0jyjlngx7y" />
                  <a href="mailto:hello@cavallasolution.com" className="underline-offset-4 hover:underline" data-id="element-609" data-mp="7bc9yq8frkgo">
                    hello@cavallasolution.com
                  </a>
                </li>
                <li className="flex gap-3" data-id="element-610" data-mp="irs30ln40dw1">
                  <MapPinIcon className="mt-1 h-4 w-4 shrink-0 text-gold" aria-hidden="true" data-id="element-611" data-mp="it376mkgvnno" />
                  <span data-id="element-612" data-mp="drnnoyxxtp7e">
                    Working across West and East Africa, with data held in the European Union.
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </Section>
    </>;
}